import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownUp,
  Check,
  ChevronDown,
  Eraser,
  ListTree,
  Pin,
  Search,
  SquarePen,
  Trash2,
  X,
} from 'lucide-react';
import { ResponseCard, type ResponseCardState } from '@/components/ResponseCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useHearingSessionContext } from '@/contexts/HearingSessionContext';
import { useMatters } from '@/contexts/MatterContext';
import { useSession } from '@/contexts/SessionContext';
import { clearResponses, deleteResponse, endSession } from '@/lib/api-client';
import type { ResponseRecord } from '@/lib/types';
import { cn } from '@/lib/cn';

// Section D: the right-column response feed. Fixed header (matter, start time,
// count) + controls (search, order toggle, quick jump) over a scrollable list
// of cards. Newest at top by default; pinned cards stick under the live card.
// Auto-scrolls to the newest card unless the operator has scrolled away. UI
// state (pins, filter, order) is persisted per session in localStorage.

type Order = 'newest' | 'oldest';

interface PersistedFeedState {
  pinnedIds: string[];
  filter: string;
  order: Order;
}

const STORAGE_PREFIX = 'cc_feed_state::';
const AT_TOP_THRESHOLD_PX = 48;

interface FeedEntry {
  attemptId: string;
  state: ResponseCardState;
}

export function SessionFeed() {
  const { feed, removeFromFeed, clearFeed } = useHearingSessionContext();
  const { sessionId, startedAt, reset: resetSession } = useSession();
  const { matters, activeMatterId } = useMatters();
  const matterName = matters.find((m) => m.id === activeMatterId)?.name ?? 'No matter';

  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [order, setOrder] = useState<Order>('newest');
  const [jumpOpen, setJumpOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const userScrolledRef = useRef(false);
  const lastTopIdRef = useRef<string | null>(null);

  // Load persisted UI state when the session changes.
  useEffect(() => {
    if (!sessionId) {
      return;
    }
    const persisted = readState(sessionId);
    setPinnedIds(persisted?.pinnedIds ?? []);
    setFilter(persisted?.filter ?? '');
    setOrder(persisted?.order ?? 'newest');
    userScrolledRef.current = false;
  }, [sessionId]);

  // Persist UI state on change.
  useEffect(() => {
    if (!sessionId) {
      return;
    }
    writeState(sessionId, { pinnedIds, filter, order });
  }, [sessionId, pinnedIds, filter, order]);

  const filtering = filter.trim().length > 0;
  const query = filter.trim().toLowerCase();

  const responseCount = useMemo(
    () => feed.filter((e) => e.state.kind === 'response').length,
    [feed],
  );

  function entryVisible(entry: FeedEntry): boolean {
    if (entry.state.kind === 'streaming') {
      return true; // the live card always shows
    }
    if (entry.state.kind === 'response') {
      if (!filtering) {
        return true;
      }
      const r = entry.state.response;
      return (
        r.answer.toLowerCase().includes(query) ||
        (r.label ?? '').toLowerCase().includes(query) ||
        r.prompt.toLowerCase().includes(query)
      );
    }
    // loading / error cards: hide while actively filtering, else show.
    return !filtering;
  }

  function responseId(entry: FeedEntry): string | null {
    return entry.state.kind === 'response' ? entry.state.response.id : null;
  }

  // Compose the rendered order: live cards, then pinned, then the rest in the
  // chosen direction. `feed` is newest-first.
  // Trial endurance (10.6 step 6): a 5-hour day accumulates hundreds of
  // responses; rendering every markdown card makes the column crawl. Cap the
  // unpinned list and reveal the rest on demand. Search bypasses the cap so
  // older responses stay findable.
  const [showAll, setShowAll] = useState(false);
  const RENDER_CAP = 60;
  const { rendered, hiddenCount } = useMemo(() => {
    const visible = feed.filter(entryVisible);
    const streaming = visible.filter((e) => e.state.kind === 'streaming');
    const settled = visible.filter((e) => e.state.kind !== 'streaming');
    const pinned = settled.filter((e) => {
      const id = responseId(e);
      return id !== null && pinnedIds.includes(id);
    });
    const rest = settled.filter((e) => {
      const id = responseId(e);
      return id === null || !pinnedIds.includes(id);
    });
    const restOrdered = order === 'newest' ? rest : [...rest].reverse();
    const capped = !filtering && !showAll && restOrdered.length > RENDER_CAP;
    const shown = capped
      ? order === 'newest'
        ? restOrdered.slice(0, RENDER_CAP)
        : restOrdered.slice(-RENDER_CAP)
      : restOrdered;
    return {
      rendered: [...streaming, ...pinned, ...shown],
      hiddenCount: capped ? restOrdered.length - RENDER_CAP : 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, pinnedIds, filter, order, showAll]);

  // Auto-scroll to the newest card when one arrives, unless the operator has
  // scrolled away. The newest card is at the top (newest order) or bottom
  // (oldest order).
  const topId = rendered[0]?.attemptId ?? null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    if (lastTopIdRef.current === topId) {
      return;
    }
    lastTopIdRef.current = topId;
    if (userScrolledRef.current) {
      return; // respect the operator's scroll position
    }
    el.scrollTo({ top: order === 'newest' ? 0 : el.scrollHeight, behavior: 'smooth' });
  }, [topId, order]);

  function handleScroll(): void {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    if (order === 'newest') {
      userScrolledRef.current = el.scrollTop > AT_TOP_THRESHOLD_PX;
    } else {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledRef.current = distanceFromBottom > AT_TOP_THRESHOLD_PX;
    }
  }

  function togglePin(id: string): void {
    setPinnedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [id, ...prev]));
  }

  // Soft-delete one response: tell the server, then drop it from the feed and
  // unpin it. On failure the card stays (the DeleteButton reverts its confirm).
  async function deleteOne(id: string): Promise<boolean> {
    if (!sessionId) {
      return false;
    }
    try {
      await deleteResponse(sessionId, id);
      removeFromFeed(id);
      setPinnedIds((prev) => prev.filter((p) => p !== id));
      return true;
    } catch (err) {
      console.warn('delete response failed:', err);
      return false;
    }
  }

  async function doClearFeed(): Promise<void> {
    if (!sessionId) {
      return;
    }
    setActionBusy(true);
    try {
      await clearResponses(sessionId);
      clearFeed();
      setPinnedIds([]);
      setClearOpen(false);
    } catch (err) {
      console.warn('clear feed failed:', err);
    } finally {
      setActionBusy(false);
    }
  }

  // End the current session (saved to Sessions, triggers its review) and open a
  // fresh one for the same matter; the feed reseeds empty automatically.
  async function doNewSession(): Promise<void> {
    setActionBusy(true);
    try {
      if (sessionId) {
        await endSession(sessionId).catch(() => undefined);
      }
      await resetSession();
      setPinnedIds([]);
      setFilter('');
      setNewSessionOpen(false);
    } catch (err) {
      console.warn('new session failed:', err);
    } finally {
      setActionBusy(false);
    }
  }

  function jumpTo(id: string): void {
    setJumpOpen(false);
    const el = cardRefs.current.get(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Quick-jump list: completed responses, newest first, with label + time.
  const jumpItems = useMemo(
    () =>
      feed
        .filter((e) => e.state.kind === 'response')
        .map((e) => (e.state.kind === 'response' ? e.state.response : null))
        .filter((r): r is ResponseRecord => r !== null),
    [feed],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Fixed header */}
      <div className="border-b border-stone-800/80 pb-2">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate font-display text-base italic text-stone-200">{matterName}</p>
          <p className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-stone-500">
            {responseCount} {responseCount === 1 ? 'response' : 'responses'}
          </p>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-600">
            {startedAt ? `Session ${formatStart(startedAt)}` : 'No active session'}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => setClearOpen(true)}
              disabled={responseCount === 0}
              title="Clear all responses from this feed"
              className="inline-flex items-center gap-1 rounded text-[10px] uppercase tracking-[0.15em] text-stone-500 transition-colors hover:text-amber-400 disabled:opacity-30 disabled:hover:text-stone-500"
            >
              <Eraser className="h-3.5 w-3.5" />
              Clear
            </button>
            <button
              type="button"
              onClick={() => setNewSessionOpen(true)}
              title="End this session and start a fresh one"
              className="inline-flex items-center gap-1 rounded border border-amber-700/50 bg-amber-950/20 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-amber-400 transition-colors hover:border-amber-600 hover:bg-amber-950/40"
            >
              <SquarePen className="h-3.5 w-3.5" />
              New session
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-600"
              aria-hidden
            />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search responses"
              className="w-full rounded-md border border-stone-800 bg-stone-900 py-1.5 pl-7 pr-7 text-xs text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none"
            />
            {filter ? (
              <button
                type="button"
                onClick={() => setFilter('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-amber-400"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setOrder((o) => (o === 'newest' ? 'oldest' : 'newest'))}
            aria-label={`Sort ${order === 'newest' ? 'oldest first' : 'newest first'}`}
            title={order === 'newest' ? 'Newest first' : 'Oldest first'}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-stone-800 bg-stone-900 px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-stone-400 transition-colors hover:border-stone-700 hover:text-amber-400"
          >
            <ArrowDownUp className="h-3.5 w-3.5" />
            {order === 'newest' ? 'New' : 'Old'}
          </button>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setJumpOpen((v) => !v)}
              disabled={jumpItems.length === 0}
              aria-label="Quick jump to a response"
              className="inline-flex items-center gap-1 rounded-md border border-stone-800 bg-stone-900 px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-stone-400 transition-colors hover:border-stone-700 hover:text-amber-400 disabled:opacity-40"
            >
              <ListTree className="h-3.5 w-3.5" />
              <ChevronDown className="h-3 w-3" />
            </button>
            {jumpOpen ? (
              <div className="absolute right-0 z-20 mt-1 max-h-72 w-60 overflow-y-auto rounded-md border border-stone-800 bg-stone-900 py-1 shadow-lg shadow-black/40">
                {jumpItems.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => jumpTo(r.id)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs text-stone-300 hover:bg-stone-800 hover:text-amber-300"
                  >
                    <span className="truncate">{formatLabel(r.label)}</span>
                    <span className="shrink-0 text-[10px] text-stone-500">
                      {formatTime(r.createdAt)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Scrollable list */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="mt-2 min-h-0 flex-1 space-y-3 overflow-y-auto pr-1"
      >
        {rendered.length === 0 ? (
          <p className="px-1 pt-3 text-sm text-stone-500">
            {filtering
              ? 'No responses match your search.'
              : 'Tap a quick action above to ask Claude. Responses appear here, newest at the top.'}
          </p>
        ) : (
          rendered.map((entry, i) => {
            const id = responseId(entry);
            const pinned = id !== null && pinnedIds.includes(id);
            return (
              <div
                key={entry.attemptId}
                ref={(node) => {
                  if (id) {
                    if (node) {
                      cardRefs.current.set(id, node);
                    } else {
                      cardRefs.current.delete(id);
                    }
                  }
                }}
                className={cn(i > 0 && 'border-t border-stone-800/50 pt-3')}
              >
                <div className="relative">
                  {id ? (
                    <div className="absolute right-1 top-1 z-10 flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => togglePin(id)}
                        aria-label={pinned ? 'Unpin response' : 'Pin response'}
                        title={pinned ? 'Unpin' : 'Pin to top'}
                        className={cn(
                          'rounded p-1 transition-colors',
                          pinned
                            ? 'text-amber-500 hover:text-amber-400'
                            : 'text-stone-600 hover:text-amber-400',
                        )}
                      >
                        <Pin className={cn('h-3.5 w-3.5', pinned && 'fill-amber-500/30')} />
                      </button>
                      <DeleteButton onDelete={() => deleteOne(id)} />
                    </div>
                  ) : null}
                  <ResponseCard state={entry.state} matterId={activeMatterId} />
                </div>
              </div>
            );
          })
        )}
        {hiddenCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full rounded-md border border-stone-800 py-2 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:border-amber-700/60 hover:text-amber-400"
          >
            Show {hiddenCount} older response{hiddenCount === 1 ? '' : 's'}
          </button>
        ) : null}
      </div>

      {clearOpen ? (
        <ConfirmDialog
          title="Clear the feed?"
          message={`This removes all ${responseCount} ${responseCount === 1 ? 'response' : 'responses'} from this session's feed. They are archived, not permanently deleted, but you cannot bring them back from here.`}
          confirmLabel="Clear feed"
          destructive
          busy={actionBusy}
          onConfirm={() => void doClearFeed()}
          onCancel={() => setClearOpen(false)}
        />
      ) : null}

      {newSessionOpen ? (
        <ConfirmDialog
          title="Start a new session?"
          message="This session is saved to Sessions (with its review) and a fresh, empty feed opens for the same matter. Your transcript so far is kept on the old session."
          confirmLabel="New session"
          busy={actionBusy}
          onConfirm={() => void doNewSession()}
          onCancel={() => setNewSessionOpen(false)}
        />
      ) : null}
    </div>
  );
}

// Per-card soft-delete with an inline two-click confirm (no modal — there can be
// many cards, and a dialog per card would be heavy mid-hearing). First click
// arms a red confirm; a second click within 4s deletes; otherwise it disarms.
function DeleteButton({ onDelete }: { onDelete: () => Promise<boolean> }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    };
  }, []);

  function disarm(): void {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    setArmed(false);
  }

  function arm(): void {
    setArmed(true);
    timer.current = window.setTimeout(() => setArmed(false), 4000);
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    const ok = await onDelete();
    // On success the card unmounts; on failure revert so the operator can retry.
    if (!ok) {
      setBusy(false);
      disarm();
    }
  }

  if (armed) {
    return (
      <span className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy}
          aria-label="Confirm delete"
          title="Confirm delete"
          className="rounded p-1 text-red-400 hover:text-red-300 disabled:opacity-40"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={disarm}
          aria-label="Cancel delete"
          title="Cancel"
          className="rounded p-1 text-stone-500 hover:text-stone-300"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={arm}
      aria-label="Delete response"
      title="Delete from feed"
      className="rounded p-1 text-stone-600 transition-colors hover:text-red-400"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

// --- localStorage helpers ---------------------------------------------------

function readState(sessionId: string): PersistedFeedState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + sessionId);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PersistedFeedState>;
    return {
      pinnedIds: Array.isArray(parsed.pinnedIds) ? parsed.pinnedIds.filter((x) => typeof x === 'string') : [],
      filter: typeof parsed.filter === 'string' ? parsed.filter : '',
      order: parsed.order === 'oldest' ? 'oldest' : 'newest',
    };
  } catch {
    return null;
  }
}

function writeState(sessionId: string, state: PersistedFeedState): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + sessionId, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable; the feed works without persistence.
  }
}

// --- formatting -------------------------------------------------------------

function formatLabel(label: string | null): string {
  if (!label) {
    return 'Custom prompt';
  }
  return label
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatStart(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
